// Quote Builder — shared products cache.
//
// Mirrors source `CAT.products` (kos-qb-v3.js:581) — the
// `/api/door-ranges` payload that all pickers fuzzy-filter against.
// The endpoint returns the full retailer-visible product list (door
// ranges plus the wider catalogue), keyed by id with productCategory
// strings the simple pickers filter on (Handles / Lighting / etc).
//
// Module-level cache with callback dedupe so the first picker to mount
// triggers the fetch and all subsequent mounts piggyback.

import { api } from "@tkos/api-client";

export interface ProductRecord {
  id: string;
  name: string;
  sku?: string;
  description?: string;
  supplierName?: string;
  category?: string;
  productCategory?: string;
  styleTags?: string[];
  finishType?: string;
  material?: string;
  priceGroup?: number;
  heroImage?: string;
  colours?: Array<{ name: string; hex?: string; availability?: string }>;
  /** Catch-all for fields we don't model strictly. */
  [k: string]: unknown;
}

interface Cache {
  products: ProductRecord[] | null;
  loading: boolean;
  callbacks: Array<(products: ProductRecord[]) => void>;
}

const CACHE: Cache = { products: null, loading: false, callbacks: [] };

export async function loadAllProducts(): Promise<ProductRecord[]> {
  if (CACHE.products) return CACHE.products;
  if (CACHE.loading) {
    return new Promise((resolve) => {
      CACHE.callbacks.push(resolve);
    });
  }
  CACHE.loading = true;
  try {
    const data = await api<{ ranges?: ProductRecord[] }>("/door-ranges");
    CACHE.products = Array.isArray(data.ranges) ? data.ranges : [];
  } catch {
    CACHE.products = [];
  }
  CACHE.loading = false;
  for (const cb of CACHE.callbacks) cb(CACHE.products);
  CACHE.callbacks = [];
  return CACHE.products;
}

/** Synchronous snapshot — returns whatever's currently in the cache.
 *  Used by render functions that want to show "Loading…" if empty. */
export function getCachedProducts(): ProductRecord[] | null {
  return CACHE.products;
}
